import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { updateVenueState } from '../../features/venue/venueSlice';
import { updateServiceProviderState } from '../../features/serviceProvider/serviceProviderSlice';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Offset from './Offset';
import EditForm from './EditForm';
import Upload from '../molecules/Upload';
import { getErrorMsg } from '../../lib/utils';
import {
  upload_image,
  delete_image,
  update_image_lexorank,
} from '../../lib/apis/image';
import styles from '../../styles/components/organisms/ItemGalleryForm.module.scss';
import { LexoRank } from 'lexorank';

interface ItemGalleryFormProps {
  venueID?: string;
  serviceProviderID?: string;
  prefill?: any;
  bg?: boolean;
  onSubmit?: (id?: string) => void;
  onCancel?: () => void;
}

const ItemGalleryForm = (props: ItemGalleryFormProps) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [removedFiles, setRemovedFiles] = useState([]) as any;
  const [newGalleryFiles, setNewGalleryFiles] = useState([]) as any;
  const { venueID, serviceProviderID, prefill, onSubmit, onCancel } = props;
  const { logo, images } = prefill;
  const imagesSorted = images.sort((a: any, b: any) => {
    return LexoRank.parse(a.lexorank).compareTo(LexoRank.parse(b.lexorank));
  });
  const [galleryFiles, setGalleryFiles] = useState(imagesSorted || []);
  const itemType = !!venueID ? 'venue' : 'service-provider';
  const itemID = venueID || serviceProviderID;
  const updateItemState = !!venueID
    ? updateVenueState
    : updateServiceProviderState;
  const formID = 'itemGalleryForm';

  /**
   * Handle errors
   */
  const handleError = (error: any, payload?: any) => {
    const errorMsg = getErrorMsg(error);
    setError(errorMsg);
    setLoading(false);
  };

  /**
   * Handle image files
   */
  const onFileRemove = (file: any) => {
    const { image_id } = file;
    if (!image_id) {
      return;
    }
    setRemovedFiles([
      ...removedFiles.filter((str: string) => str !== image_id),
      image_id,
    ]);
  };

  const updateFiles = async () => {
    for (const image_id of removedFiles) {
      await delete_image(image_id)
        .then(() => {
          console.log('deletedFile', image_id);
        })
        .catch((error: any) => {
          handleError(error);
        });
    }

    const currFiles = [
      logo,
      ...(galleryFiles || []).filter(
        (el: any) => removedFiles.indexOf(el.image_id) === -1
      ),
    ].filter((el) => !!el);

    for (const file of currFiles) {
      const { image_id, lexorank } = file;

      // If image_id is not present, it's a new file
      if (image_id) {
        await update_image_lexorank(image_id, lexorank)
          .then(() => {
            console.log('updatedFile', image_id);
          })
          .catch((error: any) => {
            handleError(error);
          });
      }
    }

    const newFiles = newGalleryFiles;
    if (!!itemID && !!newFiles.length) {
      const uploaded = [] as any;
      console.log('upload images', newFiles);
      for (const file of newFiles) {
        upload_image(itemType, 'gallery', itemID, file as any)
          .then((response: any) => {
            uploaded.push(response);
            console.log('uploaded image', response);
            if (uploaded.length === newFiles.length) {
              // field.value = '';
              completeSubmit({
                images: [...galleryFiles, ...uploaded],
              });
            }
          })
          .catch((error) => {
            handleError(error);
            return false;
          });
      }
    } else {
      completeSubmit({
        images: currFiles,
      });
    }
  };

  const completeSubmit = (result: any) => {
    const images = result.images || [];

    setRemovedFiles([]);
    setLoading(false);
    dispatch(updateItemState({ images, logo: props.prefill.logo }));
    if (!!onSubmit) {
      onSubmit();
    }
  };

  /**
   * Handle form submit
   */
  const submit = () => {
    if (error) {
      return;
    }
    setError(false);
    setLoading(true);
    updateFiles();
  };

  return (
    <EditForm
      className={styles.itemGalleryForm}
      noCols
      id={formID}
      error={error}
      loading={loading}
      onSubmit={submit}
      onCancel={onCancel}
    >
      <Row>
        <Col xs={8}>
          {/* <Property dark icon={'image'} value={t(`common:images`)} />*/}
          <br /> <br />
          <Offset className={styles.offset}>
            <Upload
              className={styles.upload}
              full
              bg
              bigThumb
              dragArea
              optional
              multiple
              hideError
              contain
              type="image"
              name={'galleryImages'}
              data={galleryFiles}
              onChange={(files) => {
                setNewGalleryFiles(files);
              }}
              onChangeOrder={(files) => {
                setGalleryFiles(files);
              }}
              onError={(error) => {
                setError(error);
              }}
              onRemove={onFileRemove}
            />
          </Offset>
        </Col>
      </Row>
    </EditForm>
  );
};

export default ItemGalleryForm;
